/**
 * ============================================================
 * KEJJA OS — src/pages/admin/AdminUsersPage.tsx (v2)
 * Clickable rows → UserDetailDrawer with full profile + actions.
 * ============================================================
 */

import { useState } from 'react';
import { Shield, Search, ChevronLeft, ChevronRight, ChevronRight as ChevronRightInd } from 'lucide-react';
import { useAdminUsers } from '@/hooks/useAdmin';
import UserDetailDrawer from '@/components/admin/UserDetailDrawer';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 25;

export default function AdminUsersPage() {
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const { data, isLoading } = useAdminUsers({ limit: PAGE_SIZE, offset: page * PAGE_SIZE, search: search || undefined });

  const users = data?.users ?? [];
  const total = data?.total ?? 0;
  const maxPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-bold">Users</h1>
            <p className="text-xs text-slate-400 mt-0.5">{total.toLocaleString()} total · click a row for full detail</p>
          </div>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              placeholder="Search email or name…"
              className="bg-slate-900 border border-slate-800 rounded-md pl-8 pr-3 h-9 text-sm text-slate-100 placeholder-slate-500 w-64 focus:outline-none focus:border-slate-600"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="text-slate-400 text-sm">Loading…</div>
        ) : (
          <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-slate-400 border-b border-slate-800">
                  <th className="p-3 font-medium">User</th>
                  <th className="p-3 font-medium">Homes</th>
                  <th className="p-3 font-medium">Joined</th>
                  <th className="p-3 font-medium">Last login</th>
                  <th className="p-3 font-medium">Status</th>
                  <th className="p-3 font-medium w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {users.map((u) => (
                  <tr key={u.id}
                    onClick={() => setSelected(u.id)}
                    className={cn(
                      'cursor-pointer hover:bg-slate-800/50 transition-colors',
                      !u.isActive && 'opacity-60',
                      u.deletedAt && 'opacity-30 line-through',
                    )}
                  >
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        {u.isSuperAdmin && <Shield className="w-3 h-3 text-rose-400 flex-shrink-0" />}
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-100 truncate">{u.displayName}</div>
                          <div className="text-slate-500 truncate">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 tabular-nums">{u.homeCount}</td>
                    <td className="p-3 text-slate-400 whitespace-nowrap">
                      {new Date(u.createdAt).toLocaleDateString('en-KE', { year: 'numeric', month: 'short', day: 'numeric' })}
                    </td>
                    <td className="p-3 text-slate-400 whitespace-nowrap">
                      {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString('en-KE', { month: 'short', day: 'numeric' }) : '—'}
                    </td>
                    <td className="p-3">
                      <span className={cn(
                        'text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap',
                        u.deletedAt      ? 'bg-rose-500/15 text-rose-400' :
                        !u.isActive      ? 'bg-amber-500/15 text-amber-400' :
                        !u.emailVerified ? 'bg-slate-700 text-slate-400' :
                                           'bg-emerald-500/15 text-emerald-400',
                      )}>
                        {u.deletedAt ? 'deleted' : !u.isActive ? 'inactive' : !u.emailVerified ? 'unverified' : 'active'}
                      </span>
                    </td>
                    <td className="p-3">
                      <ChevronRightInd className="w-3.5 h-3.5 text-slate-600" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between text-xs text-slate-400">
          <div>Page {page + 1} of {maxPage + 1}</div>
          <div className="flex gap-1">
            <button disabled={page === 0} onClick={() => setPage(page - 1)}
              className="px-2 py-1 rounded hover:bg-slate-800 disabled:opacity-30 flex items-center gap-1">
              <ChevronLeft className="w-3 h-3" /> Prev
            </button>
            <button disabled={page >= maxPage} onClick={() => setPage(page + 1)}
              className="px-2 py-1 rounded hover:bg-slate-800 disabled:opacity-30 flex items-center gap-1">
              Next <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>

      <UserDetailDrawer userId={selected} onClose={() => setSelected(null)} />
    </>
  );
}
